import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PromptVersionHistory from './PromptVersionHistory';
import PromptLockStatus from './PromptLockStatus';

interface Prompt {
  name: string;
  display_name: string;
  description: string;
  category: string;
  text: string;
  variables: string[];
  variable_warning: string;
}

// Get user email from localStorage or default
const getUserEmail = () => localStorage.getItem('user_email') || 'anonymous@user.com';
const getUserName = () => localStorage.getItem('user_name') || undefined;

interface PromptsData {
  system_type: string;
  system_name: string;
  prompts: Prompt[];
  source: string;
}

const CATEGORY_ORDER = ['cleaning', 'formatting', 'extraction', 'verification', 'deduplication', 'consolidation', 'output'];

const CATEGORY_NAMES: Record<string, string> = {
  'cleaning': 'Transcript Cleaning',
  'formatting': 'Transcript Formatting',
  'extraction': 'Fact Extraction',
  'verification': 'Verification',
  'deduplication': 'Deduplication',
  'consolidation': 'Consolidation',
  'output': 'Output Formatting'
};

const CATEGORY_ICONS: Record<string, string> = {
  'cleaning': '🧹',
  'formatting': '📝',
  'extraction': '🔍',
  'verification': '✅',
  'deduplication': '🔄',
  'consolidation': '📋',
  'output': '📄'
};

interface SavedPrompt {
  id: number;
  project_id: number | null;
  system_type: string;
  prompt_name: string;
  prompt_text: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface FactsAgentPromptsEditorProps {
  projectId?: number | null;
  onClose?: () => void;
}


export default function FactsAgentPromptsEditor({ projectId, onClose }: FactsAgentPromptsEditorProps) {
  const { isAdmin } = useAuth();
  const [prompts, setPrompts] = useState<PromptsData | null>(null);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [editedText, setEditedText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Version control state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [canEdit, setCanEdit] = useState(true);
  const userEmail = getUserEmail();
  const userName = getUserName();

  // Check if user has permission to edit (must be admin)
  const canEditPrompts = isAdmin && canEdit;

  useEffect(() => {
    loadPrompts();
  }, [projectId]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load default prompts from agents/ folder
      const defaultsResponse = await api.get('/prompts/defaults/factsagent');
      setPrompts(defaultsResponse.data);

      // Load saved project-specific prompts if project is selected
      if (projectId) {
        const savedResponse = await api.get(`/prompts/project/${projectId}?system_type=factsagent`);
        setSavedPrompts(savedResponse.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentText = (promptName: string): string => {
    // Check for saved project-specific version first
    if (projectId) {
      const saved = savedPrompts.find(p => p.prompt_name === promptName);
      if (saved) return saved.prompt_text;
    }
    // Fall back to default
    const defaultPrompt = prompts?.prompts.find(p => p.name === promptName);
    return defaultPrompt?.text || '';
  };

  const handleSelectPrompt = (promptName: string) => {
    if (hasChanges && selectedPrompt) {
      if (!confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedPrompt(promptName);
    setEditedText(getCurrentText(promptName));
    setHasChanges(false);
    setSuccessMessage(null);
  };

  const handleTextChange = (newText: string) => {
    setEditedText(newText);
    setHasChanges(newText !== getCurrentText(selectedPrompt || ''));
  };

  const handleSave = async () => {
    if (!selectedPrompt || !projectId) return;

    try {
      setSaving(true);
      setError(null);

      // Check if prompt already exists for this project
      const existingPrompt = savedPrompts.find(p => p.prompt_name === selectedPrompt);

      if (existingPrompt) {
        // Update existing with versioning
        await api.put(`/prompts/${existingPrompt.id}/versioned`, {
          prompt_text: editedText,
          change_summary: changeSummary || undefined,
          editor_email: userEmail,
          editor_name: userName
        });
      } else {
        // Create new project-specific prompt
        await api.post(`/prompts?project_id=${projectId}`, {
          system_type: 'factsagent',
          prompt_name: selectedPrompt,
          prompt_text: editedText,
          is_default: false
        });
      }

      // Reload saved prompts
      const savedResponse = await api.get(`/prompts/project/${projectId}?system_type=factsagent`);
      setSavedPrompts(savedResponse.data || []);

      setHasChanges(false);
      setChangeSummary('');
      setSuccessMessage('Prompt saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      if (err.response?.status === 423) {
        setError('Prompt is locked by another user. Cannot save.');
      } else {
        setError(err.response?.data?.detail || 'Failed to save prompt');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedPrompt || !projectId) return;

    if (!confirm('Reset this prompt to the default from the agents/ folder?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Find and delete project-specific version
      const existingPrompt = savedPrompts.find(p => p.prompt_name === selectedPrompt);
      if (existingPrompt) {
        await api.delete(`/prompts/${existingPrompt.id}`);
      }

      // Reload saved prompts
      const savedResponse = await api.get(`/prompts/project/${projectId}?system_type=factsagent`);
      setSavedPrompts(savedResponse.data || []);

      // Reset to default text
      const defaultPrompt = prompts?.prompts.find(p => p.name === selectedPrompt);
      setEditedText(defaultPrompt?.text || '');
      setHasChanges(false);
      setSuccessMessage('Prompt reset to default!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeAll = async () => {
    if (!projectId) return;

    if (!confirm('Initialize all FactsAgent prompts for this project? This will copy defaults for any prompts not yet customized.')) {
      return;
    }

    try {
      setSaving(true);
      await api.post(`/prompts/project/${projectId}/initialize?system_type=factsagent`);
      await loadPrompts();
      setSuccessMessage('All prompts initialized!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to initialize prompts');
    } finally {
      setSaving(false);
    }
  };

  const getVariables = (promptName: string): string[] => {
    const prompt = prompts?.prompts.find(p => p.name === promptName);
    return prompt?.variables || [];
  };

  const isCustomized = (promptName: string): boolean => {
    return savedPrompts.some(p => p.prompt_name === promptName);
  };

  const getSelectedPromptData = (): Prompt | null => {
    if (!selectedPrompt) return null;
    return prompts?.prompts.find(p => p.name === selectedPrompt) || null;
  };

  const selectedPromptData = getSelectedPromptData();

  // Get prompt ID for version history
  const getSelectedPromptId = (): number | null => {
    if (!selectedPrompt) return null;
    const saved = savedPrompts.find(p => p.prompt_name === selectedPrompt);
    return saved?.id || null;
  };

  const handleVersionRestore = (restoredText: string) => {
    setEditedText(restoredText);
    setHasChanges(true);
    setShowVersionHistory(false);
  };

  // Group prompts by category, with fallback for uncategorized
  const getGroupedPrompts = () => {
    if (!prompts?.prompts) return {};

    const grouped: Record<string, Prompt[]> = {};

    prompts.prompts.forEach(prompt => {
      const category = prompt.category || 'general';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(prompt);
    });

    return grouped;
  };

  const groupedPrompts = getGroupedPrompts();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="spinner mr-2"></span>
        <span className="text-gray-500">Loading prompts...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">FactsAgent Prompt Templates</h2>
          <p className="text-sm text-gray-500 mt-1">
            Edit the prompts used in the 6-step transcript processing pipeline.
            {prompts?.source && <span className="ml-1">(Source: {prompts.source})</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {projectId && isAdmin && (
            <button
              onClick={handleInitializeAll}
              disabled={saving}
              className="btn-secondary"
            >
              Initialize All for Project
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="btn-ghost">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-danger-700">{error}</span>
        </div>
      )}

      {/* Success Alert */}
      {successMessage && (
        <div className="p-4 bg-success-50 border border-success-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-success-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-success-700">{successMessage}</span>
        </div>
      )}

      {/* No Project Warning */}
      {!projectId && (
        <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-warning-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-warning-800 font-medium">View Only Mode</p>
              <p className="text-sm text-warning-700 mt-1">
                Select a project to customize prompts. You're viewing the default prompts from the agents/ folder.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Non-Admin Warning */}
      {!isAdmin && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-blue-800 font-medium">Editor Access</p>
              <p className="text-sm text-blue-700 mt-1">
                You can view prompt templates but editing requires admin privileges. Contact an administrator to request changes.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Prompt List - Grouped by Category */}
        <div className="lg:col-span-1 space-y-4">
          {Object.keys(groupedPrompts).length > 0 ? (
            Object.entries(groupedPrompts).map(([category, categoryPrompts]) => {
              if (categoryPrompts.length === 0) return null;

              return (
                <div key={category} className="card">
                  <div className="card-header bg-gray-50 py-2 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{CATEGORY_ICONS[category] || '📝'}</span>
                      <h3 className="font-semibold text-gray-700 text-sm">
                        {CATEGORY_NAMES[category] || category.charAt(0).toUpperCase() + category.slice(1)}
                      </h3>
                      <span className="text-xs text-gray-400">({categoryPrompts.length})</span>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {categoryPrompts.map((prompt) => (
                      <button
                        key={prompt.name}
                        onClick={() => handleSelectPrompt(prompt.name)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                          selectedPrompt === prompt.name ? 'bg-brand-50 border-l-4 border-brand-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900 text-sm block truncate">
                              {prompt.display_name || prompt.name}
                            </span>
                            {prompt.description && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">
                                {prompt.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            {isCustomized(prompt.name) ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                Custom
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                Global
                              </span>
                            )}
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                        {prompt.variables && prompt.variables.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            {prompt.variables.length} variable{prompt.variables.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card">
              <div className="card-body text-center py-8 text-gray-500">
                <p>No prompts available</p>
              </div>
            </div>
          )}
        </div>

        {/* Prompt Editor */}
        <div className="lg:col-span-2">
          {selectedPrompt && selectedPromptData ? (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{CATEGORY_ICONS[selectedPromptData.category] || '📝'}</span>
                      <h3 className="font-semibold text-gray-900">
                        {selectedPromptData.display_name || selectedPrompt}
                      </h3>
                      {isCustomized(selectedPrompt) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          Project Custom
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          Global
                        </span>
                      )}
                      {hasChanges && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-800">
                          Unsaved Changes
                        </span>
                      )}
                    </div>
                    {selectedPromptData.description && (
                      <p className="text-sm text-gray-500 mt-1">{selectedPromptData.description}</p>
                    )}

                    {/* Template Source Toggle */}
                    {projectId && isAdmin && (
                      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Template Source:</span>
                            {isCustomized(selectedPrompt) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                Project Custom
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Global Default
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isCustomized(selectedPrompt) ? (
                              <button
                                onClick={handleReset}
                                disabled={saving || !canEditPrompts}
                                className="btn-ghost btn-sm text-green-600 hover:bg-green-50"
                                title="Switch to using the global template"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Use Global Template
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  if (!confirm('Create a custom template for this project? You can customize it after creation.')) return;
                                  try {
                                    setSaving(true);
                                    const defaultPrompt = prompts?.prompts.find(p => p.name === selectedPrompt);
                                    if (!defaultPrompt) return;
                                    await api.post(`/prompts?project_id=${projectId}`, {
                                      system_type: 'factsagent',
                                      prompt_name: selectedPrompt,
                                      prompt_text: defaultPrompt.text,
                                      is_default: false
                                    });
                                    const savedResponse = await api.get(`/prompts/project/${projectId}?system_type=factsagent`);
                                    setSavedPrompts(savedResponse.data || []);
                                    setSuccessMessage('Custom template created! You can now customize it.');
                                    setTimeout(() => setSuccessMessage(null), 3000);
                                  } catch (err: any) {
                                    setError(err.response?.data?.detail || 'Failed to create custom template');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                                disabled={saving || !canEditPrompts}
                                className="btn-ghost btn-sm text-purple-600 hover:bg-purple-50"
                                title="Create a custom template for this project"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Create Custom Template
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {isCustomized(selectedPrompt)
                            ? 'This project uses a custom template. Click "Use Global Template" to revert to the default.'
                            : 'This project uses the global default template. Click "Create Custom Template" to customize it for this project.'}
                        </p>
                      </div>
                    )}
                    {/* Read-only Template Source Indicator for non-admins */}
                    {(!projectId || !isAdmin) && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Template Source:</span>
                        {isCustomized(selectedPrompt) ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            Project Custom
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Global Default
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Version History Button */}
                  <button
                    onClick={() => setShowVersionHistory(!showVersionHistory)}
                    className={`btn-ghost text-sm ${!getSelectedPromptId() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={getSelectedPromptId() ? 'View version history' : 'No change history yet (prompt has not been customized)'}
                    disabled={!getSelectedPromptId()}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    History
                  </button>
                </div>

                {/* Lock Status - Only show for admins */}
                {projectId && getSelectedPromptId() && isAdmin && (
                  <div className="mt-3">
                    <PromptLockStatus
                      promptId={getSelectedPromptId()}
                      userEmail={userEmail}
                      userName={userName}
                      onLockStatusChange={setCanEdit}
                    />
                  </div>
                )}
              </div>

              {/* Version History Panel */}
              {showVersionHistory && getSelectedPromptId() && (
                <div className="border-b border-gray-200 p-4 bg-gray-50">
                  <PromptVersionHistory
                    promptId={getSelectedPromptId()!}
                    promptName={selectedPromptData.display_name || selectedPrompt}
                    currentText={editedText}
                    userEmail={userEmail}
                    userName={userName}
                    onRestore={handleVersionRestore}
                    onClose={() => setShowVersionHistory(false)}
                  />
                </div>
              )}

              <div className="card-body space-y-4">
                {/* Variables Warning */}
                {getVariables(selectedPrompt).length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-amber-800">Required Variables</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Do NOT remove these placeholders:
                          <code className="ml-1 px-1 bg-amber-100 rounded">
                            {getVariables(selectedPrompt).map(v => `{${v}}`).join(', ')}
                          </code>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Editor */}
                <div>
                  <textarea
                    value={editedText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    disabled={!projectId || saving || !canEditPrompts}
                    className={`textarea font-mono text-sm h-96 ${!canEditPrompts ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Select a prompt to edit..."
                  />
                </div>

                {/* Character/Word Count */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{editedText.length.toLocaleString()} characters</span>
                  <span>{editedText.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
                </div>

                {/* Change Summary (for versioning) - Only for admins */}
                {hasChanges && projectId && isCustomized(selectedPrompt) && isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Change Summary (optional)
                    </label>
                    <input
                      type="text"
                      value={changeSummary}
                      onChange={(e) => setChangeSummary(e.target.value)}
                      placeholder="Brief description of your changes..."
                      className="input text-sm"
                      maxLength={500}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will be recorded in the version history
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {projectId && isAdmin && (
                <div className="card-footer flex items-center justify-between">
                  <button
                    onClick={handleReset}
                    disabled={saving || !isCustomized(selectedPrompt) || !canEditPrompts}
                    className="btn-secondary"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset to Default
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges || !canEditPrompts}
                    className="btn-save"
                    title={!canEditPrompts ? 'Prompt is locked by another user' : ''}
                  >
                    {saving ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="card-body flex flex-col items-center justify-center py-16 text-gray-500">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="font-medium">Select a Prompt</p>
                <p className="text-sm mt-1">Choose a prompt template from the list to view or edit</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
